/**
 * One vastuumatriisi grid: työvaiheet as rows, the sijainnit of the current
 * level as columns. Clicking a column header drills into that sijainti.
 */

import { Fragment, useMemo, useState } from "react";
import type { Location, User } from "@/types";
import {
  cellKey,
  resolveCell,
  sharedCellLabel,
  type MatrixIndex,
  type MatrixSection,
} from "@/lib/responsibilityMatrix";
import type { MatrixCell } from "@/hooks/useResponsibilityMatrix";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface Props {
  label: string;
  locations: Location[];
  sections: MatrixSection[];
  index: MatrixIndex;
  users: User[];
  canEdit: boolean;
  onAssign: (cells: MatrixCell[], userId: string | null) => Promise<void>;
}

const LOCATION_TYPE_LABELS: Record<string, string> = {
  BUILDING: "Rakennus",
  SECTION: "Lohko",
  FLOOR: "Kerros",
  ZONE: "Alue",
  SPACE: "Tila",
};

const userName = (users: User[], id: string | null) => {
  if (!id) return "–";
  const u = users.find((x) => x.id === id);
  return u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email : "–";
};

export function ResponsibilityMatrixGrid({ label, locations, sections, index, users, canEdit, onAssign }: Props) {
  const [path, setPath] = useState<Location[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<{ col: number; row: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parentId = path.length ? path[path.length - 1].id : null;
  const columns = useMemo(
    () =>
      locations
        .filter((l) => (index.parentOf.get(l.id) ?? null) === parentId)
        .sort((a, b) => a.name.localeCompare(b.name, "fi", { numeric: true })),
    [locations, index, parentId],
  );

  const levelLabel = useMemo(
    () => [...new Set(columns.map((c) => LOCATION_TYPE_LABELS[c.locationType] ?? c.locationType))].join(" / "),
    [columns],
  );

  const userOptions: ComboboxOption[] = useMemo(
    () => users.filter((u) => u.isActive).map((u) => ({ value: u.id, label: userName(users, u.id), description: u.email })),
    [users],
  );

  const keyFor = (col: number, row: number) => cellKey(columns[col].id, sections[row].sectionId);

  const toggleKeys = (keys: string[]) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = keys.every((k) => next.has(k));
      keys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });

  const onCellClick = (col: number, row: number, shift: boolean) => {
    if (!canEdit) return;
    if (shift && anchor) {
      const keys: string[] = [];
      for (let c = Math.min(anchor.col, col); c <= Math.max(anchor.col, col); c++)
        for (let r = Math.min(anchor.row, row); r <= Math.max(anchor.row, row); r++) keys.push(keyFor(c, r));
      setSelected((prev) => new Set([...prev, ...keys]));
    } else {
      toggleKeys([keyFor(col, row)]);
      setAnchor({ col, row });
    }
  };

  const drillTo = (newPath: Location[]) => {
    setPath(newPath);
    setSelected(new Set());
    setAnchor(null);
  };

  const selectedCells: MatrixCell[] = [...selected].map((k) => {
    const [locationId, sectionId] = k.split("|");
    return { locationId, sectionId };
  });
  const heldCount = selectedCells.filter((c) => resolveCell(index, c.locationId, c.sectionId).heldBySectionLocation).length;

  const apply = async (userId: string | null) => {
    setSaving(true);
    setError(null);
    try {
      await onAssign(selectedCells, userId);
      setSelected(new Set());
      setAnchor(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (columns.length === 0 || sections.length === 0) return null;

  let lastHeadsection: number | null | undefined;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-1">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm">
            {[{ id: "root", name: label }, ...path].map((crumb, i, crumbs) => {
              const isCurrent = i === crumbs.length - 1;
              return (
                <Fragment key={crumb.id}>
                  {i > 0 && <span className="text-muted-foreground">›</span>}
                  <button
                    type="button"
                    disabled={isCurrent}
                    aria-current={isCurrent ? "page" : undefined}
                    onClick={() => drillTo(path.slice(0, i))}
                    className={cn(
                      "rounded-full border px-3 py-1 font-medium transition-colors",
                      i === 0 && "font-semibold",
                      isCurrent
                        ? "cursor-default border-primary bg-primary text-primary-foreground"
                        : "bg-background hover:border-primary hover:bg-accent",
                    )}
                  >
                    {crumb.name}
                  </button>
                </Fragment>
              );
            })}
          </nav>
          <span className="rounded-full border bg-muted px-2.5 py-0.5 text-xs font-medium">
            Taso: {levelLabel}
          </span>
        </div>

        <div className="max-h-[min(480px,55dvh)] overflow-auto rounded-md border">
          <table className="text-sm border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 border-b bg-muted px-3 py-2 text-left font-medium min-w-[220px]">Työvaihe</th>
                {columns.map((col, c) => {
                  const hasChildren = (index.childrenOf.get(col.id) ?? []).length > 0;
                  const colKeys = sections.map((_, r) => keyFor(c, r));
                  return (
                    <th key={col.id} className="sticky top-0 z-20 border-b bg-muted px-3 py-2 text-left font-medium min-w-[150px] whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {canEdit && (
                          <Checkbox
                            checked={colKeys.every((k) => selected.has(k))}
                            onCheckedChange={() => toggleKeys(colKeys)}
                            aria-label={`Valitse ${col.name}`}
                          />
                        )}
                        {hasChildren ? (
                          <button
                            type="button"
                            className="rounded-full border bg-background px-3 py-1 font-medium transition-colors hover:border-primary hover:bg-accent"
                            onClick={() => drillTo([...path, col])}
                          >
                            {col.name} ›
                          </button>
                        ) : (
                          <span>{col.name}</span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sections.map((section, r) => {
                const header =
                  section.headsectionNumber !== lastHeadsection ? (
                    <tr key={`h-${section.sectionId}`}>
                      <td colSpan={columns.length + 1} className="border-t bg-muted/60 px-3 py-1 text-xs font-semibold uppercase text-muted-foreground">
                        <span className="sticky left-3">
                          {section.headsectionNumber ? `${section.headsectionNumber} ${section.headsectionLabel}` : "Muut"}
                        </span>
                      </td>
                    </tr>
                  ) : null;
                lastHeadsection = section.headsectionNumber;
                const rowKeys = columns.map((_, c) => keyFor(c, r));
                return (
                  <Fragment key={section.sectionId}>
                    {header}
                    <tr>
                      <th
                        scope="row"
                        className={cn("sticky left-0 z-10 border-t bg-background px-3 py-2 text-left font-normal", canEdit && "cursor-pointer hover:bg-accent")}
                        onClick={() => canEdit && toggleKeys(rowKeys)}
                      >
                        <span className="text-muted-foreground mr-2">{section.code}</span>
                        {section.name}
                      </th>
                      {columns.map((col, c) => {
                        const state = resolveCell(index, col.id, section.sectionId);
                        const key = keyFor(c, r);
                        const holders = [
                          ...new Set([
                            ...(state.userId ? [state.userId] : []),
                            ...state.exceptions.map((ex) => ex.responsibleUserId),
                          ]),
                        ];
                        const nameClass = state.source === "set" ? "text-foreground" : "text-muted-foreground";
                        return (
                          <td
                            key={col.id}
                            onClick={(e) => onCellClick(c, r, e.shiftKey)}
                            className={cn(
                              "border-t px-3 py-2 select-none whitespace-nowrap",
                              canEdit && "cursor-pointer hover:bg-accent",
                              selected.has(key) && "bg-primary/15 ring-1 ring-inset ring-primary",
                            )}
                          >
                            {holders.length <= 1 ? (
                              <span className={nameClass}>{userName(users, state.userId)}</span>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className={nameClass}>{sharedCellLabel(holders, users)}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <ul className="space-y-0.5">
                                    {state.exceptions.map((ex) => (
                                      <li key={ex.locationId}>
                                        {locations.find((l) => l.id === ex.locationId)?.name}: {userName(users, ex.responsibleUserId)}
                                      </li>
                                    ))}
                                  </ul>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {canEdit && selected.size > 0 && (
          <div className="sticky bottom-0 z-20 flex flex-wrap items-center gap-3 rounded-md border bg-background p-3 shadow-md">
            <span className="text-sm font-medium">{selected.size === 1 ? '1 solu valittu' : `${selected.size} solua valittu`}</span>
            <div className="w-64">
              <Combobox
                options={userOptions}
                value={null}
                onChange={(v) => v && apply(v)}
                placeholder="Valitse vastuuhenkilö"
                searchPlaceholder="Hae käyttäjää"
                disabled={saving}
                loading={saving}
              />
            </div>
            <Button variant="outline" size="sm" disabled={saving} onClick={() => apply(null)}>
              Periytä
            </Button>
            <Button variant="ghost" size="sm" disabled={saving} onClick={() => setSelected(new Set())}>
              Tyhjennä valinta
            </Button>
            {heldCount > 0 && (
              <span className="text-xs text-amber-700">
                {heldCount} valitussa solussa Tehtävät-välilehdellä asetettu vastuuhenkilö ohittaa tämän valinnan
              </span>
            )}
            {error && <span className="text-xs text-red-700">{error}</span>}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
